import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subject, EMPTY, interval, forkJoin, of } from 'rxjs';
import { takeUntil, takeWhile, tap, switchMap, catchError } from 'rxjs/operators';

import { PlanosService } from '../../services/planos/planos.service';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';
import { DashboardService } from '../../services/dashboard/dashboard.service';
import { ContadorService } from '../../services/contador/contador.service';
import { IndicacaoService } from '../../services/indicacoes/indicacao.service';
import { ContratacaoUsuariosService } from '../../services/usuario/contratacao-usuarios.service';
import { SafeStorageService } from '../../shared/safe-storage.service';
import { PeriodoPlano } from '../../enums/PeriodoPlano.enum';
import { Plano, IntencaoInscricao, Inscricao, Cupom, LimiteDeUsuarios } from '../../models/planos/plano.model';
import { MeuDescontoVigenteDTO } from '../../models/contador/contador.model';
import { DescontoIndicacaoPendenteConsultaDTO } from '../../models/indicacoes/indicacao.model';
import { aplicarDescontoContador } from '../../shared/utils/desconto-contador.util';

@Component({
  selector: 'app-planos',
  templateUrl: './planos.component.html',
  styleUrls: ['./planos.component.scss'],
  providers: [MessageService],
})
export class PlanosComponent implements OnInit, OnDestroy {

  // Estado principal
  planos: Plano[] = [];
  planosFiltrados: Plano[] = [];
  isLoading: boolean = false;
  periodoSelecionado: PeriodoPlano = PeriodoPlano.MENSAL;
  opcoesPeriodo: { label: string; value: PeriodoPlano; tag?: string }[] = [];

  // Estado de checkout
  planoSelecionado: Plano | null = null;
  intencaoInscricao: IntencaoInscricao | null = null;
  urlQrCode: string | null = null;
  alias: string = '';
  planoAssinado: boolean = false;
  exibirErro: boolean = false;

  // Timers
  tempoRestante: number = 300;
  tempoFormatado: string = '05:00';
  qtdVerificacoes: number = 0;

  // Cupom
  cupomTexto: string = '';
  cupomAplicado: Cupom | null = null;
  cupomAprovado: string = '';
  carregandoCupom: boolean = false;
  /** Carregamento do botao "Contratar" (cupom de cortesia, sem gateway). */
  carregandoContratacao: boolean = false;

  // Plano atual / Contexto
  inscricaoAtiva: Inscricao | null = null;
  totalVendas: number = 0;
  // Desconto do contador vigente para a empresa autenticada (null = sem contrato ATIVO
  // ou falha no endpoint). Incide sobre plano.valor no front, com precedencia sobre o cupom.
  descontoContadorVigente: MeuDescontoVigenteDTO | null = null;
  // Desconto de indicacao (R15) elegivel geral da empresa (null = sem indicacao/cupom
  // valido, contador ATIVO, ou falha no endpoint). Dirige os CARDS. Tem precedencia
  // MENOR que contador e que cupom digitado aplicado (nunca exibir dois descontos).
  descontoIndicacaoPendente: DescontoIndicacaoPendenteConsultaDTO | null = null;
  // Desconto de indicacao REFINADO por plano (inclui trava R14/plano gratuito),
  // recarregado ao abrir o dialogo de um plano especifico. Dirige o DIALOGO.
  descontoIndicacaoPlano: DescontoIndicacaoPendenteConsultaDTO | null = null;
  // Quando true (via query param ?renovar=true vindo do banner do dashboard),
  // abre direto o dialog de pagamento do plano contratado assim que planos e
  // inscricao carregarem.
  private deveRenovar = false;
  // Planos AvatiPro sao comercializados SEMPRE em Guarani (PYG), independente
  // da moeda padrao configurada na empresa. Nao sobrescrever em
  // carregarContextoEmpresa().
  readonly moeda: string = 'PYG';
  readonly locale: string = 'es-PY';

  // Alerta e Dialog
  exibirBannerLimite: boolean = false;
  /** Veio da tela de usuários porque as vagas acabaram (`?limiteUsuarios=true`). */
  exibirBannerLimiteUsuarios: boolean = false;
  exibirDialogPagamento: boolean = false;
  exibirDialogCiclo: boolean = false;
  cancelDialogVisible: boolean = false;
  cancelamentoPendente: boolean = false;
  planoParaDialog: Plano | null = null;

  // Usuários de acesso adicionais escolhidos no checkout. Abre com o que a empresa já tem
  // (renovação carrega adiante) e é cobrado pelo período inteiro do plano.
  usuariosExtras: number = 0;
  readonly MAX_USUARIOS_EXTRAS = 100;

  // Retrato dos usuários de acesso da empresa, exibido no card do plano atual.
  // null = ainda carregando ou falha no endpoint: a linha simplesmente não aparece.
  limiteUsuarios: LimiteDeUsuarios | null = null;

  @ViewChild('cancelConfirmBtn') cancelConfirmBtnRef!: ElementRef<HTMLButtonElement>;

  // Subscription cleanup
  private destroy$ = new Subject<void>();
  private pollingStop$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private planosService: PlanosService,
    private empresaService: EmpresaConfigService,
    private dashboardService: DashboardService,
    private contadorService: ContadorService,
    private indicacaoService: IndicacaoService,
    private contratacaoUsuariosService: ContratacaoUsuariosService,
    private storageService: SafeStorageService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.buildOpcoesPeriodo();
    this.verificarQueryParams();
    this.carregarContextoEmpresa();
    this.carregarPlanos();
    this.carregarLimiteUsuarios();

    // Atualizar labels de periodo quando idioma mudar
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.buildOpcoesPeriodo();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.pollingStop$.next();
    this.pollingStop$.complete();
  }

  // =========================================================================
  // Metodos publicos
  // =========================================================================

  alterarPeriodo(evento: { value?: PeriodoPlano }): void {
    if (!evento.value) return;
    this.periodoSelecionado = evento.value;
    this.filtrarPlanos();
  }

  abrirDialogPagamento(plano: Plano): void {
    this.planoParaDialog = plano;
    this.cupomTexto = '';
    this.cupomAplicado = null;
    this.cupomAprovado = '';
    // Renovação carrega os usuários adicionais adiante: o campo já abre com o que a empresa
    // tem hoje, então quem só quer renovar não perde acesso nenhum ao confirmar.
    this.usuariosExtras = this.inscricaoAtiva?.usuariosExtras ?? 0;
    // Inicia o dialogo com o desconto de indicacao geral (otimista) e refina por
    // plano (a resposta pode virar possuiDesconto=false por R14/plano gratuito).
    this.descontoIndicacaoPlano = this.descontoIndicacaoPendente;
    this.exibirDialogPagamento = true;
    // So refina quando faz sentido (sem contador — precedencia). Em falha, mantem o geral.
    if (!this.temDescontoContador) {
      this.indicacaoService.getMeuDescontoIndicacao(plano.id)
        .pipe(catchError(() => of(this.descontoIndicacaoPendente)))
        .subscribe((res) => {
          this.descontoIndicacaoPlano = res ?? null;
        });
    }
  }

  assinarPlano(): void {
    if (!this.planoParaDialog) return;

    this.zerarVariaveis();
    const plano = this.planoParaDialog;

    this.planosService.assinarPlano(plano.id, this.cupomAprovado || undefined, this.usuariosExtras).subscribe({
      next: (intencao: IntencaoInscricao) => {
        if (intencao.linkUrl) {
          this.exibirDialogPagamento = false;
          this.planoSelecionado = plano;
          this.intencaoInscricao = intencao;
          this.alias = intencao.linkAlias || '';
          window.open(intencao.linkUrl, '_blank');
          this.iniciarPolling(false);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('planos.link_indisponivel'),
          });
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: err.error?.message || this.translate.instant('planos.link_indisponivel'),
        });
      },
    });
  }

  gerarQrCode(): void {
    if (!this.planoParaDialog) return;

    this.zerarVariaveis();
    const plano = this.planoParaDialog;

    this.planosService.gerarQrCode(plano.id, this.cupomAprovado || undefined, this.usuariosExtras).subscribe({
      next: (intencao: IntencaoInscricao) => {
        if (intencao.urlQr) {
          this.exibirDialogPagamento = false;
          this.planoSelecionado = plano;
          this.intencaoInscricao = intencao;
          this.urlQrCode = intencao.urlQr;
          this.alias = intencao.hookAlias || '';
          this.cupomAplicado = null;
          this.cupomAprovado = '';
          this.iniciarPolling(true);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('planos.qrcode_indisponivel'),
          });
        }
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: err.error?.message || this.translate.instant('planos.qrcode_indisponivel'),
        });
      },
    });
  }

  verificarCupom(): void {
    if (!this.cupomTexto || !this.cupomTexto.trim()) return;

    const cupomNome = this.cupomTexto.trim().toUpperCase();
    this.carregandoCupom = true;

    // O plano escolhido vai junto: e com ele que o backend valida a faixa de um cupom
    // especial e ja devolve as condicoes negociadas para exibirmos aqui.
    this.planosService.verificarCupom(cupomNome, this.planoParaDialog?.id).subscribe({
      next: (cupom: Cupom) => {
        this.cupomAplicado = cupom;
        this.cupomAprovado = cupom.nome;
        this.carregandoCupom = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('planos.cupom_aplicado_sucesso'),
        });
      },
      error: (err) => {
        this.cupomAplicado = null;
        this.cupomAprovado = '';
        this.carregandoCupom = false;
        // Preferir a mensagem localizada do backend (ex.: uso do proprio cupom
        // de indicacao); cair no texto generico quando ausente.
        this.messageService.add({
          severity: 'error',
          summary: err.error?.message || this.translate.instant('planos.cupom_invalido'),
        });
      },
    });
  }

  valorComDesconto(valor: number): number | null {
    // Cupom especial nao desconta: ele define o preco. Nao ha percentual a aplicar sobre o
    // valor de tabela — o valor negociado ja e o final.
    if (this.temCupomEspecial) return this.cupomAplicado!.valorPlano ?? null;
    if (!this.cupomAplicado || !this.cupomAplicado.porcentagemDesconto) return null;
    return valor - (valor * (this.cupomAplicado.porcentagemDesconto / 100));
  }

  // -------------------------------------------------------------------------
  // Cupom especial — o cupom DEFINE o plano (valor, periodo e limite), em vez de descontar
  // -------------------------------------------------------------------------

  /**
   * Ha um cupom especial aplicado e valido para o plano aberto no dialogo. Nao exige valor:
   * o cupom de CORTESIA tambem e especial (define plano, prazo e limite), so nao tem o que
   * cobrar — ver [temCupomCortesia].
   */
  get temCupomEspecial(): boolean {
    return !!this.cupomAplicado?.especial;
  }

  /** Valor fechado da contratacao negociada (Gs). */
  get valorCupomEspecial(): number | null {
    return this.temCupomEspecial ? (this.cupomAplicado!.valorPlano ?? 0) : null;
  }

  /** Vigencia negociada, em dias. */
  get periodoCupomEspecial(): number | null {
    return this.temCupomEspecial ? (this.cupomAplicado!.periodoDias ?? null) : null;
  }

  /** Limite mensal de vendas negociado (Gs). */
  get limiteCupomEspecial(): number | null {
    return this.temCupomEspecial ? (this.cupomAplicado!.limiteDeGasto ?? null) : null;
  }

  /**
   * Cupom especial de CORTESIA: o plano negociado nao tem valor a cobrar, entao o dialogo troca
   * "Assinar/QR Code" por um unico "Contratar" — nao ha pagamento a abrir.
   */
  get temCupomCortesia(): boolean {
    return !!this.cupomAplicado?.especial && !!this.cupomAplicado?.semCobranca;
  }

  /**
   * Contrata direto, sem gateway. A assinatura volta ja ativa, entao nao ha polling: basta
   * recarregar o contexto para a tela refletir o plano novo.
   */
  contratarComCupom(): void {
    if (!this.planoParaDialog || !this.cupomAprovado) return;

    this.carregandoContratacao = true;
    this.planosService.contratarComCupom(this.planoParaDialog.id, this.cupomAprovado).subscribe({
      next: () => {
        this.carregandoContratacao = false;
        this.exibirDialogPagamento = false;
        this.cupomAplicado = null;
        this.cupomAprovado = '';
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('planos.contratacao_sucesso'),
        });
        this.carregarContextoEmpresa();
        this.carregarPlanos();
      },
      error: (err) => {
        this.carregandoContratacao = false;
        this.messageService.add({
          severity: 'error',
          summary: err.error?.message || this.translate.instant('planos.erro_contratacao'),
        });
      },
    });
  }

  // -------------------------------------------------------------------------
  // Desconto do contador (eixo independente do cupom — tem precedencia)
  // -------------------------------------------------------------------------

  get temDescontoContador(): boolean {
    return !!this.descontoContadorVigente
      && this.descontoContadorVigente.percentualDesconto > 0;
  }

  get percentualContador(): number | undefined {
    return this.descontoContadorVigente?.percentualDesconto;
  }

  get nomeContador(): string | undefined {
    return this.descontoContadorVigente?.nomeContador;
  }

  /** Preco com desconto do contador para o dialogo. null se gratuito (RN07) ou sem desconto. */
  valorComDescontoContador(plano: Plano): number | null {
    if (!this.temDescontoContador) return null;
    if (plano.planoStart === true || plano.valor === 0) return null; // RN07
    return aplicarDescontoContador(plano.valor, this.descontoContadorVigente!.percentualDesconto);
  }

  // -------------------------------------------------------------------------
  // Desconto de indicacao (R15) — precedencia MENOR: contador > cupom > indicacao
  // -------------------------------------------------------------------------

  /** Desconto de indicacao vigente para os CARDS (visao geral, sem planoId). */
  get temDescontoIndicacao(): boolean {
    return !this.temDescontoContador
      && !!this.descontoIndicacaoPendente?.possuiDesconto
      && (this.descontoIndicacaoPendente?.porcentagemDesconto ?? 0) > 0;
  }

  get percentualIndicacao(): number | undefined {
    return this.descontoIndicacaoPendente?.porcentagemDesconto ?? undefined;
  }

  get codigoCupomIndicacao(): string | undefined {
    return this.descontoIndicacaoPendente?.codigoCupom ?? undefined;
  }

  /**
   * Desconto de indicacao para o DIALOGO (refinado por plano). Suprimido quando ha
   * desconto de contador OU cupom digitado aplicado (precedencia — nunca dois descontos).
   */
  get temDescontoIndicacaoDialog(): boolean {
    return !this.temDescontoContador
      && !this.cupomAplicado
      && !!this.descontoIndicacaoPlano?.possuiDesconto
      && (this.descontoIndicacaoPlano?.porcentagemDesconto ?? 0) > 0;
  }

  /**
   * Oculta o campo de cupom no dialogo quando ja ha um desconto que NAO acumula
   * com cupom digitado: desconto de contador OU desconto de indicacao vigente
   * para o plano (R15). Espelha o comportamento do contador — se o desconto some
   * (cupom vinculado vencido sem fallback, R14 ja usado, plano gratuito), o
   * `possuiDesconto` cai para false e o campo volta. Baseado em `descontoIndicacaoPlano`
   * (nao em `temDescontoIndicacaoDialog`, que embute `!cupomAplicado`): o gatilho de
   * ESCONDER depende so do desconto de indicacao, nao de estado de cupom digitado.
   */
  get bloquearCampoCupom(): boolean {
    // Um cupom ESPECIAL vence contador e indicacao (ele nao desconta, define a contratacao),
    // entao esconder o campo deixaria o cliente de contador sem como digita-lo. Ja aplicado,
    // o campo continua visivel para ele poder trocar ou remover o cupom.
    if (this.temCupomEspecial) return false;
    return this.temDescontoContador
      || (!!this.descontoIndicacaoPlano?.possuiDesconto
        && (this.descontoIndicacaoPlano?.porcentagemDesconto ?? 0) > 0);
  }

  get percentualIndicacaoDialog(): number | undefined {
    return this.descontoIndicacaoPlano?.porcentagemDesconto ?? undefined;
  }

  get codigoCupomIndicacaoDialog(): string | undefined {
    return this.descontoIndicacaoPlano?.codigoCupom ?? undefined;
  }

  // -------------------------------------------------------------------------
  // Usuarios de acesso adicionais contratados junto com o plano
  // -------------------------------------------------------------------------

  /** Meses do periodo do plano — base da cobranca dos usuarios adicionais no checkout. */
  private mesesDoPlano(plano: Plano): number {
    switch (plano.tipoPlano) {
      case 'MENSAL': return 1;
      case 'TRIMESTRAL': return 3;
      case 'SEMESTRAL': return 6;
      case 'ANUAL': return 12;
      default: return 0; // ESPECIAL nao vence
    }
  }

  /** Preco mensal do usuario adicional. Fallback so protege plano antigo sem o campo. */
  precoMensalUsuario(plano: Plano): number {
    return plano.valorPorUsuarioAdicional ?? 0;
  }

  /** Usuarios que o plano ja inclui (1 por padrao: o dono da empresa). */
  usuariosInclusos(plano: Plano): number {
    return plano.usuariosInclusos ?? 1;
  }

  /**
   * Quanto os usuarios adicionais somam nesta contratacao:
   * `quantidade x meses do plano x preco mensal`. Mesma conta do backend — o valor exibido
   * aqui e o mesmo que sera cobrado.
   */
  valorUsuariosExtras(plano: Plano): number {
    if (!this.usuariosExtras || this.usuariosExtras <= 0) return 0;
    return this.usuariosExtras * this.mesesCobradosNoDialog(plano) * this.precoMensalUsuario(plano);
  }

  /**
   * Meses cobrados dos usuarios adicionais neste dialogo. Com cupom especial sao os meses
   * equivalentes ao periodo NEGOCIADO (mes comecado, mes cobrado — mesma conta do backend);
   * sem cupom especial, os meses da periodicidade do plano.
   */
  private mesesCobradosNoDialog(plano: Plano): number {
    const dias = this.periodoCupomEspecial;
    if (dias && dias > 0) return Math.max(Math.ceil(dias / 30), 1);
    return this.mesesDoPlano(plano);
  }

  /**
   * Total do checkout: plano + usuarios adicionais, com o desconto vigente aplicado sobre o
   * TOTAL (mesma regra do backend — o desconto nao para na mensalidade do plano).
   */
  valorTotalDialog(plano: Plano): number {
    // Com cupom especial, a base e o valor negociado (nao o de tabela) e os usuarios
    // adicionais seguem os meses do periodo negociado.
    if (this.temCupomEspecial) {
      return (this.valorCupomEspecial ?? 0) + this.valorUsuariosExtras(plano);
    }
    const base = plano.valor + this.valorUsuariosExtras(plano);
    const percentual = this.percentualDescontoVigenteDialog(plano);
    if (!percentual) return base;
    return aplicarDescontoContador(base, percentual);
  }

  /** Percentual que incide no dialogo, na mesma precedencia do backend: contador > cupom > indicacao. */
  private percentualDescontoVigenteDialog(plano: Plano): number | null {
    if (plano.planoStart === true || plano.valor === 0) return null;
    // Cupom especial nao entra na conta de percentual: o valor dele ja e o final.
    if (this.temCupomEspecial) return null;
    if (this.temDescontoContador) return this.percentualContador ?? null;
    if (this.cupomAplicado) return this.cupomAplicado.porcentagemDesconto ?? null;
    if (this.temDescontoIndicacaoDialog) return this.percentualIndicacaoDialog ?? null;
    return null;
  }

  /** Plano gratuito e ESPECIAL nao tem periodo a cobrar: o seletor de usuarios nem aparece. */
  podeContratarUsuariosNoDialog(plano: Plano): boolean {
    // Cortesia nao passa por cobranca: usuario adicional e pago, entao nao cabe aqui. Quem
    // precisar de mais acessos contrata pelo fluxo avulso depois de contratar o plano.
    if (this.temCupomCortesia) return false;
    return plano.planoStart !== true && this.mesesDoPlano(plano) > 0;
  }

  alterarUsuariosExtras(delta: number): void {
    const novo = (this.usuariosExtras ?? 0) + delta;
    this.usuariosExtras = Math.min(Math.max(novo, 0), this.MAX_USUARIOS_EXTRAS);
  }

  /** Preco com desconto de indicacao para o dialogo. null se gratuito ou sem desconto. */
  valorComDescontoIndicacaoDialog(plano: Plano): number | null {
    if (!this.temDescontoIndicacaoDialog) return null;
    if (plano.planoStart === true || plano.valor === 0) return null;
    return aplicarDescontoContador(plano.valor, this.descontoIndicacaoPlano!.porcentagemDesconto!);
  }

  cancelarTransacao(): void {
    if (this.intencaoInscricao?.linkAlias) {
      // Cancelar transacao T-Pago
      this.planosService.cancelarTransacaoTPago(this.intencaoInscricao.linkAlias).subscribe({
        next: () => {
          this.zerarVariaveis();
          this.planoSelecionado = null;
        },
        error: () => {
          this.zerarVariaveis();
          this.planoSelecionado = null;
        },
      });
    } else {
      // QR Code - apenas limpar estado
      this.zerarVariaveis();
      this.planoSelecionado = null;
    }
  }

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent): void {
    if (this.cancelDialogVisible) {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.cancelarAssinatura();
        return;
      }
      if (ev.key === 'Escape') {
        ev.preventDefault();
        this.cancelDialogVisible = false;
        return;
      }
    }
  }

  confirmarCancelamento(): void {
    if (!this.inscricaoAtiva) return;
    this.cancelDialogVisible = true;
  }

  onCancelDialogShow(): void {
    setTimeout(() => {
      this.cancelConfirmBtnRef?.nativeElement?.focus();
    }, 50);
  }

  cancelarAssinatura(): void {
    this.cancelDialogVisible = false;
    if (!this.inscricaoAtiva) return;

    const aliasResolve = this.inscricaoAtiva.linkAlias
      || this.inscricaoAtiva.hookAlias
      || '';

    this.planosService.cancelarAssinatura(aliasResolve).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('planos.cancelamento_sucesso'),
        });
        this.cancelamentoPendente = true;
        this.carregarContextoEmpresa();
        this.carregarPlanos();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('planos.erro_cancelamento'),
        });
      },
    });
  }

  /**
   * Plano ESPECIAL (ex.: "Plan Contador"): perpétuo, sem vencimento e sem limite
   * de vendas. O card "Plano atual" exibe "sem vencimento"/"sem limite" em vez de
   * data vazia e oculta o fechamento do ciclo.
   */
  get ehPlanoEspecial(): boolean {
    return this.inscricaoAtiva?.plano?.tipoPlano === 'ESPECIAL';
  }

  /**
   * Limite mensal de vendas que vale na assinatura atual.
   *
   * Sai de `limiteDeGastoEfetivo` — o campo que o backend calcula e que ja considera o limite
   * NEGOCIADO por cupom especial. Ler `plano.limiteDeGasto` aqui mostraria zero para quem
   * contratou por cupom, porque o plano-recipiente nao tem limite proprio. O `??` cobre
   * respostas de versoes antigas do backend.
   */
  get limiteDeVendasVigente(): number {
    return this.inscricaoAtiva?.limiteDeGastoEfetivo
      ?? this.inscricaoAtiva?.plano?.limiteDeGasto
      ?? 0;
  }

  // Percentual de uso do limite do plano atual (para a barra de progresso)
  get percentualUsoLimite(): number {
    const limite = this.limiteDeVendasVigente;
    if (limite <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((this.totalVendas / limite) * 100));
  }

  // Data em que a contagem de vendas do mes reinicia (fechamento do ciclo
  // de 30 dias), baseada no dia da data de ativacao da assinatura.
  get dataReinicioContagem(): string {
    const dataAtivacao = this.parsearData(this.inscricaoAtiva?.dataDeInscricao);
    if (!dataAtivacao) return '';
    return this.formatarData(this.calcularProximaDataFechamentoCiclo(dataAtivacao));
  }

  // Verdadeiro apenas durante o periodo de carencia: APOS o vencimento da
  // assinatura e ate 5 dias depois (vencido, mas ainda dentro da carencia).
  get estaNoPeriodoDeCarencia(): boolean {
    const vencimento = this.parsearData(this.inscricaoAtiva?.dataDeCancelamento);
    if (!vencimento) return false;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    vencimento.setHours(0, 0, 0, 0);
    const fimDaCarencia = new Date(vencimento);
    fimDaCarencia.setDate(fimDaCarencia.getDate() + 5); // 5 dias de carencia
    return hoje.getTime() > vencimento.getTime() &&
           hoje.getTime() <= fimDaCarencia.getTime();
  }

  // Proxima data de reinicio do ciclo de 30 dias, contada a partir da data
  // de ativacao (contratacao). O ciclo reinicia a cada 30 dias: ativacao +
  // 30, + 60, + 90... Retorna a proxima data de reinicio a partir de hoje.
  private calcularProximaDataFechamentoCiclo(dataAtivacao: Date): Date {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    let k = 1;
    let proxima = new Date(
      dataAtivacao.getFullYear(), dataAtivacao.getMonth(), dataAtivacao.getDate() + 30 * k);
    while (proxima.getTime() < hoje.getTime()) {
      k++;
      proxima = new Date(
        dataAtivacao.getFullYear(), dataAtivacao.getMonth(), dataAtivacao.getDate() + 30 * k);
    }
    return proxima;
  }

  // Converte data do backend (ISO 8601 ou timestamp em ms) em Date.
  private parsearData(data: string | undefined): Date | null {
    if (!data) return null;
    // Data "YYYY-MM-DD" deve ser interpretada como LOCAL. new Date(string)
    // a trata como UTC e, em fusos negativos (ex.: Paraguai UTC-4), recua 1 dia.
    const iso = String(data).substring(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (m) {
      return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }
    let d = new Date(data);
    if (isNaN(d.getTime())) {
      const ts = Number(data);
      if (isNaN(ts)) return null;
      d = new Date(ts);
      if (isNaN(d.getTime())) return null;
    }
    return d;
  }

  private formatarData(d: Date): string {
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const ano = d.getFullYear();
    return `${dia}/${mes}/${ano}`;
  }

  ehPlanoContratado(plano: Plano): boolean {
    return this.inscricaoAtiva?.plano?.id === plano.id && this.inscricaoAtiva?.status === true;
  }

  // Badge "Popular": Plata no mensal, Ouro no trimestral/anual
  ehPlanoDestaqueCalc(plano: Plano): boolean {
    const planosPagos = this.planosFiltrados.filter(p => p.valor > 0 && p.planoStart !== true);

    if (this.periodoSelecionado === PeriodoPlano.MENSAL) {
      if (planosPagos.length < 2) return false;
      return plano.id === planosPagos[1].id; // Plata
    }

    // Trimestral e Anual: destaque no Ouro (terceiro plano pago)
    if (planosPagos.length < 3) return false;
    return plano.id === planosPagos[2].id; // Ouro
  }

  fecharErro(): void {
    this.exibirErro = false;
    this.planoSelecionado = null;
  }

  acessarPagamento(): void {
    if (this.intencaoInscricao?.linkUrl) {
      window.open(this.intencaoInscricao.linkUrl, '_blank');
    }
  }

  formatarDataInscricao(data: string | undefined): string {
    // Reutiliza parsearData (interpreta "YYYY-MM-DD" como local, evitando o
    // recuo de 1 dia por fuso) e formatarData para a saida dd/MM/yyyy.
    const d = this.parsearData(data);
    return d ? this.formatarData(d) : '';
  }

  // =========================================================================
  // Metodos privados
  // =========================================================================

  private carregarPlanos(): void {
    this.isLoading = true;
    this.planosService.listarPlanos().subscribe({
      next: (planos: Plano[]) => {
        this.planos = planos;
        this.calcularValoresOriginais();
        this.buildOpcoesPeriodo();
        this.filtrarPlanos();
        this.isLoading = false;
        this.tentarAbrirDialogRenovacao();
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private filtrarPlanos(): void {
    this.planosFiltrados = this.planos.filter(
      p => p.tipoPlano === this.periodoSelecionado
    );
  }

  private calcularValoresOriginais(): void {
    this.planos.forEach(plano => {
      this.calcularValorOriginal(plano);
    });
  }

  private calcularValorOriginal(plano: Plano): void {
    if (plano.valor === 0 || !plano.porcentagemDesconto || plano.porcentagemDesconto <= 0) return;

    const fator = 1 - (plano.porcentagemDesconto / 100);
    if (fator > 0) {
      plano.valorOriginalSemDesconto = Math.round((plano.valor / fator) * 100) / 100;
    }
  }

  private iniciarPolling(ehQrCode: boolean): void {
    // Resetar timers
    this.tempoRestante = 300;
    this.tempoFormatado = '05:00';
    this.qtdVerificacoes = 0;
    this.planoAssinado = false;

    // Parar polling anterior
    this.pollingStop$.next();

    // Timer de countdown (1 segundo)
    interval(1000).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.pollingStop$),
      takeWhile(() => this.tempoRestante > 0 && !this.planoAssinado),
      tap(() => {
        this.tempoRestante--;
        const min = Math.floor(this.tempoRestante / 60);
        const seg = this.tempoRestante % 60;
        this.tempoFormatado = `${String(min).padStart(2, '0')}:${String(seg).padStart(2, '0')}`;
        if (this.tempoRestante <= 0) {
          this.onPollingTimeout();
        }
      })
    ).subscribe();

    // Timer de verificacao (5 segundos)
    interval(5000).pipe(
      takeUntil(this.destroy$),
      takeUntil(this.pollingStop$),
      takeWhile(() => this.qtdVerificacoes < 60 && !this.planoAssinado),
      tap(() => this.qtdVerificacoes++),
      switchMap(() =>
        this.planosService.verificarPagamento(this.alias).pipe(
          catchError(() => EMPTY)
        )
      ),
    ).subscribe({
      next: (inscricao: Inscricao) => {
        // Pagamento confirmado
        this.inscricaoAtiva = inscricao;
        this.planoAssinado = true;
        this.pollingStop$.next();
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('planos.plano_assinado_sucesso'),
        });
      },
    });
  }

  private onPollingTimeout(): void {
    this.pollingStop$.next();
    this.exibirErro = true;
    this.planoSelecionado = null;
  }

  private zerarVariaveis(): void {
    this.pollingStop$.next();
    this.intencaoInscricao = null;
    this.urlQrCode = null;
    this.alias = '';
    this.planoAssinado = false;
    this.exibirErro = false;
    this.tempoRestante = 300;
    this.tempoFormatado = '05:00';
    this.qtdVerificacoes = 0;
  }

  private verificarQueryParams(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['limiteAtingido'] === 'true') {
          this.exibirBannerLimite = true;
        }
        // Sem o banner, quem foi desviado pela tela de usuários chega aqui sem saber por quê.
        if (params['limiteUsuarios'] === 'true') {
          this.exibirBannerLimiteUsuarios = true;
        }
        if (params['renovar'] === 'true') {
          this.deveRenovar = true;
          this.tentarAbrirDialogRenovacao();
        }
      });
  }

  /**
   * Usuários de acesso da empresa, exibidos no card do plano atual.
   *
   * Falha aqui não pode atrapalhar a tela de planos: sem o limite, a linha de usuários
   * simplesmente não aparece e o resto do card segue igual.
   */
  private carregarLimiteUsuarios(): void {
    this.contratacaoUsuariosService.consultarLimite()
      .pipe(takeUntil(this.destroy$), catchError(() => of(null)))
      .subscribe((limite) => {
        this.limiteUsuarios = limite ?? null;
      });
  }

  private carregarContextoEmpresa(): void {
    const idEmpresa = this.storageService.getItem('id');
    if (!idEmpresa) return;

    // Buscar configuracao da empresa e total de vendas do mes em paralelo,
    // igual ao dashboard — o endpoint /configuracaoEmpresa/{id} nao retorna totalVendas
    forkJoin({
      empresa: this.empresaService.get(`/configuracaoEmpresa/${idEmpresa}`).pipe(catchError(() => of(null))),
      totalVendasMes: this.dashboardService.getTotalVendasNoMes().pipe(catchError(() => of(0))),
      // Desconto do contador: degrada para "sem desconto" (preco cheio) em qualquer falha (RNF03).
      desconto: this.contadorService.getMeuDescontoVigente().pipe(catchError(() => of(null))),
      // Desconto de indicacao (R15) elegivel geral (sem planoId). Em paralelo — o
      // backend ja devolve possuiDesconto=false quando ha contador ATIVO (precedencia),
      // e o front reforca a precedencia descartando este quando ha desconto de contador.
      descontoIndicacao: this.indicacaoService.getMeuDescontoIndicacao().pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ empresa, totalVendasMes, desconto, descontoIndicacao }) => {
        // moeda/locale propositalmente NAO sao lidos da empresa: planos
        // AvatiPro sao sempre exibidos e cobrados em Guarani (PYG/es-PY).
        // O endpoint /configuracaoEmpresa/{id} retorna ConfiguracaoEmpresaConsultaDTO,
        // cujo campo se chama inscricaoAtual (nao inscricaoAtualAtiva)
        if (empresa?.inscricaoAtual) {
          this.inscricaoAtiva = empresa.inscricaoAtual;
          this.selecionarPeriodoDoPlanoContratado();
          this.tentarAbrirDialogRenovacao();
        }
        // Total de vendas vem do endpoint dedicado /nota/valor_de_venda_no_mes
        this.totalVendas = totalVendasMes as number;
        // Corpo vazio (200) => null (empresa sem contrato de contador ATIVO).
        this.descontoContadorVigente = desconto ?? null;
        // Precedencia: se ha desconto de contador, NAO guarda o de indicacao
        // (nunca exibir dois descontos). Os getters tambem reforcam essa regra.
        this.descontoIndicacaoPendente = this.temDescontoContador ? null : (descontoIndicacao ?? null);
      },
    });
  }

  // Abre a tela ja na aba (Mensual/Trimestral/Anual) do periodo do plano
  // contratado. Se o plano nao tiver aba correspondente (ex.: SEMESTRAL,
  // ESPECIAL ou gratuito), mantem o periodo padrao (MENSAL).
  private selecionarPeriodoDoPlanoContratado(): void {
    const tipo = this.inscricaoAtiva?.plano?.tipoPlano;
    if (tipo && (Object.values(PeriodoPlano) as string[]).includes(tipo)) {
      this.periodoSelecionado = tipo as PeriodoPlano;
      // Reaplica o filtro para a lista de planos refletir o periodo selecionado
      // (o p-selectButton atualiza sozinho via ngModel, mas filtrarPlanos so
      // roda no onChange disparado pelo usuario).
      this.filtrarPlanos();
    }
  }

  // Abre o dialog de pagamento do plano contratado para renovacao. So executa
  // quando planos e inscricao ja carregaram (e o flag deveRenovar esta ativo);
  // por isso e chamado nos dois fluxos assincronos. Abre apenas uma vez.
  private tentarAbrirDialogRenovacao(): void {
    if (!this.deveRenovar) return;
    if (!this.planos.length || !this.inscricaoAtiva?.plano) return;
    const planoContratado = this.planos.find(p => p.id === this.inscricaoAtiva?.plano?.id);
    if (!planoContratado) return;
    this.deveRenovar = false;
    this.abrirDialogPagamento(planoContratado);
  }

  private buildOpcoesPeriodo(): void {
    const descontoTrimestral = this.obterDescontoPorPeriodo('TRIMESTRAL');
    const descontoAnual = this.obterDescontoPorPeriodo('ANUAL');

    const labelTrimestral = descontoTrimestral
      ? this.translate.instant('planos.trimestral') + ` ${descontoTrimestral}% ` + this.translate.instant('planos.off')
      : this.translate.instant('planos.trimestral');

    const labelAnual = descontoAnual
      ? this.translate.instant('planos.anual') + ` ${descontoAnual}% ` + this.translate.instant('planos.off')
      : this.translate.instant('planos.anual');

    this.opcoesPeriodo = [
      {
        label: this.translate.instant('planos.mensal'),
        value: PeriodoPlano.MENSAL,
      },
      {
        label: labelTrimestral,
        value: PeriodoPlano.TRIMESTRAL,
      },
      {
        label: labelAnual,
        value: PeriodoPlano.ANUAL,
        tag: this.translate.instant('planos.mais_procurado'),
      },
    ];
  }

  private obterDescontoPorPeriodo(tipoPlano: string): number | null {
    const plano = this.planos.find(p => p.tipoPlano === tipoPlano && p.porcentagemDesconto && p.porcentagemDesconto > 0);
    return plano?.porcentagemDesconto ?? null;
  }
}
